import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, type Dispatch } from 'react';
import { invalidateTraining, queryKeys } from '../../api/keys';
import { ApiRequestError } from '../../lib/api';
import { fetchUploadStatus } from '../../api/uploads';
import type { UploadAction } from './uploadQueue';

export const UPLOAD_POLL_MS = 2000;
export const MAX_POLL_ERRORS = 3;
export const POLL_ERROR_MESSAGE = "Couldn't check this upload's status.";

export function UploadStatusPoller({ id, uploadKey, dispatch }: { id: string; uploadKey: string; dispatch: Dispatch<UploadAction> }): null {
  const queryClient = useQueryClient();
  const errors = useRef(0);
  useQuery({
    queryKey: queryKeys.upload(uploadKey),
    queryFn: async () => {
      try {
        const status = await fetchUploadStatus(uploadKey);
        errors.current = 0;
        dispatch({ type: 'status', id, status });
        // A settled status takes the item out of 'processing', which unmounts this poller, so this runs once.
        if (status.status !== 'pending') void invalidateTraining(queryClient);
        return status;
      } catch (error) {
        errors.current += 1;
        if (errors.current >= MAX_POLL_ERRORS || (error instanceof ApiRequestError && error.status === 401)) {
          dispatch({ type: 'error', id, message: POLL_ERROR_MESSAGE });
        }
        throw error;
      }
    },
    refetchInterval: UPLOAD_POLL_MS,
    retry: false,
  });
  return null;
}
